/**
 * MMX Symon Says — typewriter headline.
 *
 * The headline keeps "SyMON SAYS" static and types/deletes the phrase underneath it,
 * cycling through the merchant's "Typed phrase" blocks. A phrase with a photo swaps it in
 * behind the POP badge as that phrase starts typing. (The social wall that used to follow
 * it is now its own section: assets/mmx-social-wall.js.)
 */

const TYPE_SPEED = 55;
const DELETE_SPEED = 28;
const HOLD_AFTER_TYPE = 2200;
const HOLD_AFTER_DELETE = 400;

class MmxSymonSays extends HTMLElement {
  connectedCallback() {
    this.phraseEl = this.querySelector('[data-symon-phrase]');
    this.liveEl = this.querySelector('[data-symon-live]');
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.#setupEntrance();
    this.#setupPhrases();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.observer?.disconnect();
    this.entranceObserver?.disconnect();
    this.resizeObserver?.disconnect();
  }

  /* --------------------------------- Entrance --------------------------------- */

  // Hides the portrait until the section is on screen, then lets the CSS pop it in once.
  // Without the script it simply stays visible.
  #setupEntrance() {
    if (!this.querySelector('.mmx-symon__chef')) return;

    this.classList.add('is-armed');
    this.entranceObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.classList.add('is-in-view');
        this.entranceObserver.disconnect();
      },
      { threshold: 0.25 }
    );
    this.entranceObserver.observe(this.querySelector('.mmx-symon__chef'));
  }

  /* --------------------------------- Typewriter --------------------------------- */

  #setupPhrases() {
    if (!this.phraseEl) return;

    let phrases = [];
    try {
      phrases = JSON.parse(this.dataset.phrases || '[]');
    } catch (error) {
      phrases = [];
    }
    phrases = phrases.map((phrase) => String(phrase).trim()).filter(Boolean);

    if (phrases.length === 0) return;

    this.phrases = phrases;
    this.index = 0;
    this.#render(phrases[0]);

    if (phrases.length === 1) return;

    // Phrases wrap to different line counts, so reserve the tallest up front: the artwork
    // underneath must not jump every time the headline cycles.
    this.#lockPhraseHeight();
    this.resizeObserver = new ResizeObserver(() => this.#lockPhraseHeight());
    this.resizeObserver.observe(this);

    // Only animate while the headline is actually on screen.
    this.observer = new IntersectionObserver((entries) => {
      const visible = entries.some((entry) => entry.isIntersecting);
      if (visible) {
        this.#start();
      } else {
        clearTimeout(this.timer);
      }
    });
    this.observer.observe(this);
  }

  #start() {
    clearTimeout(this.timer);

    if (this.reduceMotion.matches) {
      // No typing — just swap the phrase on a slow cycle.
      const cycle = () => {
        this.index = (this.index + 1) % this.phrases.length;
        this.#render(this.phrases[this.index]);
        this.#showSlide(this.index);
        this.timer = setTimeout(cycle, HOLD_AFTER_TYPE);
      };
      this.timer = setTimeout(cycle, HOLD_AFTER_TYPE);
      return;
    }

    this.classList.add('is-typing');
    this.timer = setTimeout(() => this.#deleteStep(), HOLD_AFTER_TYPE);
  }

  #deleteStep() {
    const current = this.phrases[this.index];
    const shown = this.shownLength ?? current.length;

    if (shown <= 0) {
      this.index = (this.index + 1) % this.phrases.length;
      this.shownLength = 0;
      this.#showSlide(this.index);
      this.timer = setTimeout(() => this.#typeStep(), HOLD_AFTER_DELETE);
      return;
    }

    this.shownLength = shown - 1;
    this.#render(current.slice(0, this.shownLength));
    this.timer = setTimeout(() => this.#deleteStep(), DELETE_SPEED);
  }

  #typeStep() {
    const current = this.phrases[this.index];

    if (this.shownLength >= current.length) {
      this.liveEl && (this.liveEl.textContent = current);
      this.timer = setTimeout(() => this.#deleteStep(), HOLD_AFTER_TYPE);
      return;
    }

    this.shownLength += 1;
    this.#render(current.slice(0, this.shownLength));
    this.timer = setTimeout(() => this.#typeStep(), TYPE_SPEED);
  }

  /**
   * Both photo frames (back and front) hold a slide per phrase photo. A phrase without a
   * photo keeps whatever is showing. The outgoing slide stays underneath until the new
   * one has finished opening over it.
   */
  #showSlide(index) {
    const incoming = this.querySelectorAll(`[data-symon-slide="${index}"]`);
    if (incoming.length === 0 || incoming[0].classList.contains('is-active')) return;

    const animate = !this.reduceMotion.matches;

    this.querySelectorAll('.mmx-symon__photo').forEach((frame) => {
      const next = frame.querySelector(`[data-symon-slide="${index}"]`);
      if (!next) return;

      frame.querySelectorAll('.mmx-symon__slide.is-leaving').forEach((slide) => {
        slide.classList.remove('is-leaving');
      });
      const current = frame.querySelector('.mmx-symon__slide.is-active');
      current?.classList.remove('is-active', 'is-entering');

      // Hidden slides are lazy; make sure the next one is fetched before it opens.
      next.loading = 'eager';
      next.classList.add('is-active');

      if (!animate) return;

      current?.classList.add('is-leaving');
      next.classList.remove('is-entering');
      void next.offsetWidth;
      next.classList.add('is-entering');
      next.addEventListener(
        'animationend',
        () => {
          next.classList.remove('is-entering');
          current?.classList.remove('is-leaving');
        },
        { once: true }
      );
    });
  }

  #lockPhraseHeight() {
    const markup = this.phraseEl.innerHTML;
    const shown = this.shownLength;
    this.phraseEl.style.minHeight = '';

    let tallest = 0;
    this.phrases.forEach((phrase) => {
      this.#render(phrase);
      tallest = Math.max(tallest, this.phraseEl.offsetHeight);
    });

    this.phraseEl.style.minHeight = `${tallest}px`;
    this.phraseEl.innerHTML = markup;
    this.shownLength = shown;
  }

  /**
   * The caret has to sit on the same line as the last word, so the trailing word and the
   * caret are wrapped together in a nowrap span instead of being separate inline boxes.
   */
  #render(text) {
    this.shownLength = text.length;

    const caret = '<span class="mmx-symon__caret" aria-hidden="true"></span>';
    const breakIndex = text.lastIndexOf(' ');
    const head = breakIndex === -1 ? '' : text.slice(0, breakIndex + 1);
    const tail = breakIndex === -1 ? text : text.slice(breakIndex + 1);

    this.phraseEl.innerHTML = `${escapeHtml(head)}<span class="mmx-symon__tail">${escapeHtml(tail)}${caret}</span>`;
  }
}

function escapeHtml(value) {
  return value.replace(/[&<>"']/g, (char) => {
    switch (char) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });
}

if (!customElements.get('mmx-symon-says')) {
  customElements.define('mmx-symon-says', MmxSymonSays);
}
